import { useState, useEffect, useCallback } from "react";

export type Route =
  | { view: "live" }
  | { view: "sessions"; mode: "list" }
  | { view: "sessions"; mode: "session"; sessionId: number }
  | { view: "sessions"; mode: "compare"; sessionAId: number; sessionBId: number }
  | { view: "settings" }
  | { view: "settings"; tab: string }
  | { view: "pitboard" };

export type ViewName = Route["view"];

export function parseHash(hash: string): Route {
  const path = hash.replace(/^#\/?/, "");
  const segments = path.split("/").filter(Boolean);
  const view = segments[0] || "live";

  switch (view) {
    case "sessions": {
      const idA = parseInt(segments[1] ?? "", 10);
      if (isNaN(idA)) return { view: "sessions", mode: "list" };
      if (segments[2] === "vs") {
        const idB = parseInt(segments[3] ?? "", 10);
        if (!isNaN(idB)) {
          return {
            view: "sessions",
            mode: "compare",
            sessionAId: idA,
            sessionBId: idB,
          };
        }
      }
      return { view: "sessions", mode: "session", sessionId: idA };
    }
    case "settings":
      if (segments[1]) return { view: "settings", tab: segments[1] };
      return { view: "settings" };
    case "pitboard":
      return { view: "pitboard" };
    default:
      return { view: "live" };
  }
}

export function routeToHash(route: Route): string {
  switch (route.view) {
    case "live":
      return "#/live";
    case "sessions":
      if (route.mode === "session") return `#/sessions/${route.sessionId}`;
      if (route.mode === "compare") {
        return `#/sessions/${route.sessionAId}/vs/${route.sessionBId}`;
      }
      return "#/sessions";
    case "settings":
      if ("tab" in route) return `#/settings/${route.tab}`;
      return "#/settings";
    case "pitboard":
      return "#/pitboard";
  }
}

export function useRouter() {
  const [route, setRoute] = useState<Route>(() =>
    parseHash(window.location.hash),
  );

  useEffect(() => {
    const syncFromHash = () => {
      const next = parseHash(window.location.hash);
      const canonical = routeToHash(next);
      if (window.location.hash && window.location.hash !== canonical) {
        window.history.replaceState(null, "", canonical);
      }
      setRoute(next);
    };

    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, []);

  const navigate = useCallback((next: Route) => {
    window.location.hash = routeToHash(next);
  }, []);

  return { route, navigate };
}
